import React, { useMemo } from 'react';
import type { Schedule, Task } from '../types';
import { formatUserTimezone } from '../utils/timezone';

interface MonthCalendarViewProps {
  schedule: Schedule;
  reservations: Task[];  // Changed from Reservation[] to Task[]
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  onDayClick: (date: Date) => void;
}

const MonthCalendarView: React.FC<MonthCalendarViewProps> = ({
  schedule,
  reservations,
  selectedDate,
  onDateChange,
  onDayClick,
}) => {
  // Get days in month (in local timezone)
  const monthDays = useMemo(() => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    
    // Create first and last day in local time
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    let startDay = firstDay.getDay();
    startDay = startDay === 0 ? 6 : startDay - 1; // Convert to Monday = 0
    
    const days: (Date | null)[] = [];
    
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }
    
    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  }, [selectedDate]);

  // Get reservations for a specific day (local time comparison)
  const getReservationsForDay = (date: Date | null): Task[] => {
    if (!date) return [];
    
    // Get date boundaries in local timezone (start and end of day)
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    
    return reservations.filter(res => {
      // Parse reservation start time (already in UTC from backend)
      const resStart = new Date(res.start_time);
      
      // Check if reservation starts on this day (compare UTC components)
      // But account for timezone - the day in local time might be different from UTC day
      const resStartLocal = new Date(resStart);
      const resYear = resStartLocal.getFullYear();
      const resMonth = resStartLocal.getMonth();
      const resDay = resStartLocal.getDate();
      
      // Compare local date components (after timezone conversion)
      return (
        resYear === year &&
        resMonth === month &&
        resDay === day
      );
    });
  };

  // Check if day is working day (UTC)
  const isWorkingDay = (date: Date | null): boolean => {
    if (!date || !schedule.working_hours) {
      return true; // If no working hours defined, all days are available
    }

    // Handle different data formats
    let workingHoursArray: any[] = [];
    
    if (Array.isArray(schedule.working_hours)) {
      workingHoursArray = schedule.working_hours;
    } else if (typeof schedule.working_hours === 'string') {
      try {
        const parsed = JSON.parse(schedule.working_hours);
        workingHoursArray = Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.error('Error parsing working_hours JSON:', e);
        return true; // Default to available if parsing fails
      }
    } else if (typeof schedule.working_hours === 'object') {
      // Handle object format (e.g., {monday: [...], tuesday: [...]})
      const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      workingHoursArray = dayNames.map((dayName, index) => {
        const dayData = (schedule.working_hours as any)[dayName];
        return {
          weekday: index,
          is_active: Array.isArray(dayData) && dayData.length > 0
        };
      });
    }

    if (workingHoursArray.length === 0) {
      return true; // If no working hours defined, all days are available
    }

    const dayOfWeek = date.getDay(); // Use local day
    const weekday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    return workingHoursArray.some(wh => wh.weekday === weekday && wh.is_active);
  };

  // Check if day is today (local time)
  const isToday = (date: Date | null): boolean => {
    if (!date) return false;
    const today = new Date();
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  };

  // Check if day is in past (local time)
  const isPast = (date: Date | null): boolean => {
    if (!date) return false;
    const today = new Date();
    const todayLocal = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    const dateLocal = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
    return dateLocal < todayLocal;
  };

  // Navigate months
  const goToPreviousMonth = () => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() - 1);
    onDateChange(newDate);
  };

  const goToNextMonth = () => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + 1);
    onDateChange(newDate);
  };

  const goToToday = () => {
    onDateChange(new Date());
  };

  const getMonthName = (): string => {
    return selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      {/* Calendar Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button
            onClick={goToPreviousMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={goToNextMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <button
            onClick={goToToday}
            className="px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition"
          >
            Today
          </button>
        </div>
        <h2 className="text-xl font-bold text-gray-900">{getMonthName()}</h2>
        <div className="w-32"></div> {/* Spacer for centering */}
      </div>

      {/* Calendar Grid */}
      <div className="p-6">
        {/* Weekday Headers */}
        <div className="grid grid-cols-7 gap-2 mb-2">
          {weekDays.map((day) => (
            <div key={day} className="text-center text-sm font-semibold text-gray-700 py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Days Grid */}
        <div className="grid grid-cols-7 gap-2">
          {monthDays.map((date, index) => {
            if (!date) {
              return <div key={`empty-${index}`} className="aspect-square" />;
            }

            const dayReservations = getReservationsForDay(date);
            const isWorking = isWorkingDay(date);
            const isPastDay = isPast(date);
            const isTodayDay = isToday(date);

            return (
              <div
                key={index}
                onClick={() => {
                  // Allow click on any future date (regardless of working hours)
                  if (!isPastDay) {
                    onDayClick(date);
                  }
                }}
                className={`aspect-square border-2 rounded-lg p-2 transition ${
                  isTodayDay
                    ? 'border-primary-600 bg-primary-100 cursor-pointer hover:shadow-md ring-2 ring-primary-300'
                    : isPastDay
                    ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                    : dayReservations.length > 0
                    ? 'border-green-300 bg-green-50 cursor-pointer hover:shadow-md'
                    : !isWorking
                    ? 'border-gray-300 bg-gray-100 cursor-pointer hover:shadow-md'
                    : 'border-gray-200 bg-white cursor-pointer hover:border-primary-400 hover:shadow-md'
                }`}
              >
                {/* Day Number */}
                <div className={`text-right text-sm font-semibold mb-1 ${
                  isTodayDay
                    ? 'text-primary-700'
                    : isPastDay
                    ? 'text-gray-400'
                    : 'text-gray-900'
                }`}>
                  {date.getDate()}
                </div>

                {/* Reservations Indicators */}
                <div className="space-y-1">
                  {dayReservations.slice(0, 3).map((reservation) => {
                    return (
                      <div
                        key={reservation.id}
                        className={`text-xs px-2 py-1 rounded truncate ${
                          reservation.source === 'google'
                            ? 'bg-purple-500 text-white'
                            : 'bg-primary-600 text-white'
                        }`}
                        title={reservation.client_name}
                      >
                        {reservation.client_name}
                      </div>
                    );
                  })}
                  {dayReservations.length > 3 && (
                    <div className="text-xs text-gray-600 pl-2">
                      +{dayReservations.length - 3} more
                    </div>
                  )}
                </div>

                {/* Working Day Indicator */}
                {!isWorking && !isPastDay && (
                  <div className="text-xs text-gray-500 mt-1">Not working</div>
                )}

                {/* Past Day Indicator */}
                {isPastDay && (
                  <div className="text-xs text-gray-400 mt-1">Past</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="px-6 py-4 border-t border-gray-200 flex flex-wrap items-center justify-between text-xs text-gray-600">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center">
            <div className="w-4 h-4 border-2 border-primary-600 bg-primary-50 rounded mr-2"></div>
            <span>Today</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 border-2 border-green-300 bg-green-50 rounded mr-2"></div>
            <span>Has tasks</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 border-2 border-gray-200 bg-white rounded mr-2"></div>
            <span>Available</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 border-2 border-gray-300 bg-gray-100 rounded mr-2"></div>
            <span>Not working</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 border-2 border-gray-200 bg-gray-50 rounded mr-2 opacity-60"></div>
            <span>Past</span>
          </div>
        </div>
        
        {/* Local Timezone Indicator */}
        <div className="flex items-center px-3 py-1 bg-primary-50 rounded-full border border-primary-200">
          <svg className="h-3 w-3 mr-1 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="font-semibold text-primary-700 text-xs">Local: {formatUserTimezone()}</span>
        </div>
      </div>
    </div>
  );
};

export default MonthCalendarView;

